/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * PullToRefreshSpinner - iOS-style circular spinner
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { motion } from 'framer-motion'
import PropTypes from 'prop-types'

/**
 * iOS-style circular spinner for pull-to-refresh
 *
 * @param {Object} props
 * @param {number} props.progress - Pull progress from 0 to 1
 * @param {boolean} props.isRefreshing - Whether refresh is in progress
 * @param {number} props.size - Spinner size in pixels
 */
const PullToRefreshSpinner = ({ progress = 0, isRefreshing = false, size = 32 }) => {
  // Circle properties
  const strokeWidth = 2.5
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference - progress * circumference

  return (
    <motion.div
      className="flex items-center justify-center"
      animate={{
        rotate: isRefreshing ? 360 : 0
      }}
      transition={{
        rotate: {
          duration: 1,
          repeat: isRefreshing ? Infinity : 0,
          ease: 'linear'
        }
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-gray-200 dark:text-gray-700"
        />

        {/* Progress circle */}
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          className="text-primary-600 dark:text-primary-400"
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: isRefreshing ? circumference * 0.25 : strokeDashoffset
          }}
          animate={{
            strokeDashoffset: isRefreshing ? circumference * 0.25 : strokeDashoffset
          }}
          transition={{ duration: 0.1 }}
        />
      </svg>
    </motion.div>
  )
}

PullToRefreshSpinner.propTypes = {
  progress: PropTypes.number,
  isRefreshing: PropTypes.bool,
  size: PropTypes.number
}

export default PullToRefreshSpinner
